import { Link } from 'react-router-dom';
import { Apple, MonitorSmartphone } from 'lucide-react';
import { Logo } from './Logo';
import { SITE } from '../lib/site';

const WindowsIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M3 5.55 10.4 4.5v7H3zM11.4 4.35 21 3v8.5h-9.6zM3 12.5h7.4v7L3 18.45zM11.4 12.5H21V21l-9.6-1.35z" />
  </svg>
);

const columns = [
  {
    title: 'Platform',
    links: [
      { label: 'Home', to: '/' },
      { label: 'Products', to: '/products' },
      { label: 'FAQ', to: '/faq' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us', to: '/about' },
      { label: 'Contact Us', to: '/contact' },
      { label: 'Careers', to: '/contact' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Terms of Use', to: '/terms-of-service' },
      { label: 'Privacy Policy', to: '/privacy-policy' },
      { label: 'Refund Policy', to: '/refund-policy' },
      { label: 'Cookie Policy', to: '/cookie-policy' },
    ],
  },
];

const AppButton = ({ href, icon, label, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="group flex min-w-0 flex-1 items-center gap-3 rounded-2xl border border-brand-line bg-white px-4 py-3 shadow-card transition-all duration-200 hover:-translate-y-1 hover:border-brand-lineBlue hover:shadow-lift sm:px-5"
  >
    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-ice text-brand-navy transition-colors group-hover:bg-brand-sky group-hover:text-white">
      {icon}
    </span>
    <span className="text-left">
      <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-400">Download for</span>
      <span className="block text-sm font-extrabold text-brand-navy">{label}</span>
    </span>
  </a>
);

export const Footer = () => (
  <footer className="border-t border-brand-line bg-brand-iceSoft" data-testid="site-footer">
    <div className="container-x grid gap-12 py-16 lg:grid-cols-[1.3fr_2fr] lg:gap-20 lg:py-20">
      <div className="max-w-md">
        <Link to="/" aria-label="CapableBee home">
          <Logo />
        </Link>
        <p className="mt-5 text-sm leading-relaxed text-brand-body/80">
          AI voice assistants that answer your business calls 24/7 — capturing leads, booking appointments and
          automating conversations.
        </p>

        <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-brand-skyDeep">Get the App</p>
        <div className="mt-4 flex gap-3">
          <AppButton href={SITE.downloads.windows} icon={<WindowsIcon className="h-4 w-4" />} label="Windows" testid="footer-app-windows" />
          <AppButton href={SITE.downloads.macos} icon={<Apple className="h-4 w-4" />} label="macOS" testid="footer-app-macos" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:pt-2">
        {columns.map((col) => (
          <nav key={col.title} aria-label={`Footer ${col.title}`} data-testid={`footer-column-${col.title.toLowerCase()}`}>
            <h3 className="text-sm font-extrabold text-brand-navy">{col.title}</h3>
            <ul className="mt-5 space-y-3.5">
              {col.links.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm font-medium text-brand-body/80 transition-colors hover:text-brand-skyDeep"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>
    </div>

    <div className="border-t border-brand-line/70">
      <div className="container-x flex flex-col items-center justify-between gap-3 py-6 text-sm text-brand-body/70 sm:flex-row">
        <p data-testid="footer-copyright">© 2026 CapableBee. All rights reserved.</p>
        <p className="inline-flex items-center gap-2">
          Built for growing businesses.
          <MonitorSmartphone className="h-4 w-4 text-brand-skyDeep" aria-hidden="true" />
        </p>
      </div>
    </div>
  </footer>
);
